/**
 * Created: 2026-10-29
 * Initial commit: Move event seeding helpers from mini into shared for web reuse
 *
 * Player/team seeding helpers and start-format resolution shared by `mini/` and
 * `web/` when starting an event.
 */

import { EventPlanner } from './planner'
import { GameFormat } from './types'

export type SeedingEntry = {
  rating: number
  signupTime: number // ms since epoch
}

export function sortBySeeding<T extends SeedingEntry>(entries: T[]): T[] {
  return [...entries].sort((a, b) => b.rating - a.rating || a.signupTime - b.signupTime)
}

/**
 * Groups consecutive items sharing the same key into sub-arrays, preserving order.
 * Used to group a seeding-ordered match side's players into fixed-partner Doubles
 * teams (by competitorIndex), so a team's seeding number can be rendered once,
 * centered across the whole team, instead of once per player.
 */
export function groupConsecutiveByKey<T>(
  items: T[],
  keyOf: (item: T) => number,
): T[][] {
  const groups: T[][] = []
  for (const item of items) {
    const key = keyOf(item)
    const lastGroup = groups[groups.length - 1]
    if (lastGroup && keyOf(lastGroup[0]) === key) {
      lastGroup.push(item)
    } else {
      groups.push([item])
    }
  }
  return groups
}

/**
 * Seeds fixed-partner Doubles teams the same way individual players are seeded:
 * by rating descending, earliest signup time ascending as a tiebreak. A team's
 * rating is the average of its members' ratings; its signup time is the earliest
 * of its members' signup times. Each group is expected to be a fixed-size team
 * (e.g. a pair from TeamPicker); member order within a group is preserved.
 */
export function sortTeamsBySeeding<T extends SeedingEntry>(teams: T[][]): T[][] {
  const averageRating = (team: T[]) =>
    team.reduce((sum, p) => sum + p.rating, 0) / team.length
  const earliestSignup = (team: T[]) => Math.min(...team.map((p) => p.signupTime))

  return [...teams].sort(
    (a, b) =>
      averageRating(b) - averageRating(a) || earliestSignup(a) - earliestSignup(b),
  )
}

/**
 * Returns the format to use when starting an event, or null if the user must choose.
 * Logic: find all formats supported for the current player count; if only one is
 * available use it directly; if more than one is available, match against the
 * event's own format when it unambiguously names a supported one (or ask the user
 * otherwise, e.g. when the event is Freeplay). Fixed-partner Doubles is never
 * auto-resolved even when it's the event's tagged format — it always requires the
 * organizer to manually assign teams via TeamPicker — but it still counts toward
 * "more than one format available", so its presence can force Singles/RotatingDoubles
 * through the ambiguous (null) path too.
 */
export function resolveStartFormat(
  playerCount: number,
  eventFormat: GameFormat,
): GameFormat | null {
  const singlesOk = EventPlanner.supportsPlayerCount(playerCount, GameFormat.Singles)
  const rotatingDoublesOk = EventPlanner.supportsPlayerCount(
    playerCount,
    GameFormat.RotatingDoubles,
  )
  const fixedDoublesOk = EventPlanner.supportsPlayerCount(
    playerCount,
    GameFormat.Doubles,
  )

  const supportedFormatCount = [singlesOk, rotatingDoublesOk, fixedDoublesOk].filter(
    Boolean,
  ).length

  if (supportedFormatCount === 0) return null
  if (supportedFormatCount === 1) {
    if (singlesOk) return GameFormat.Singles
    if (rotatingDoublesOk) return GameFormat.RotatingDoubles
    return null // fixedDoublesOk alone — still requires manual team pick
  }

  if (singlesOk && eventFormat === GameFormat.Singles) return GameFormat.Singles
  if (rotatingDoublesOk && eventFormat === GameFormat.RotatingDoubles) {
    return GameFormat.RotatingDoubles
  }
  return null // e.g. Freeplay, Doubles, or a format not supported at this count
}
